import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { store } from "./Redux/store";
import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";
import { ThemeProvider } from "@material-ui/styles";
import { createMuiTheme, responsiveFontSizes } from "@material-ui/core";
import { toast } from "react-toastify";
import { consumeAuthBridge } from "./utils/authBridge";
import { bootstrapSessionFromCookie } from "./utils/bootstrapSessionFromCookie";
import { registerAuthSyncListener } from "./utils/authSyncListener";
import {
  SESSION_EXPIRED_FLAG,
  SESSION_EXPIRED_MESSAGE,
} from "./Core/sessionExpired";
import { preloadSandbox } from "./mfe/preloadSandbox";
import { decrypt } from "./utils/other";
import App from "./AppRouter.jsx";
import ErrorFallback from "./Components/ErrorFallback/ErrorFallback";

import "./index.scss";
import "./fonts.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-toastify/dist/ReactToastify.css";

// -------------------------------------------------------------------------
// MFE — Start downloading sandbox assets early, in parallel with portal render.
// To remove: delete this call and src/mfe/preloadSandbox.js.
// -------------------------------------------------------------------------
preloadSandbox();

// -------------------------------------------------------------------------
// Sentry
// -------------------------------------------------------------------------
const stripBridgeParams = (url) => {
  if (typeof url !== "string") return url;
  return url
    .replace(/([?&])_spb=[^&]*/g, "$1")
    .replace(/[?&]$/, "")
    .replace(/\?&/, "?");
};

Sentry.init({
  // Env-driven so stage builds can point elsewhere; falls back to the prod DSN
  // so an unconfigured build still reports.
  dsn:
    process.env.REACT_APP_SENTRY_DSN ||
    "https://665a813f015b4d919e92365dd5f7c6cf@o877499.ingest.sentry.io/6238621",
  integrations: [new Integrations.BrowserTracing()],
  // 1.0 shipped a full trace for every session and burned transaction quota.
  tracesSampleRate: 0.1,
  environment: process.env.REACT_APP_SENTRY_ENV || "production",
  // Local dev events used to ship into the production project.
  enabled: process.env.NODE_ENV !== "development",
  beforeBreadcrumb(breadcrumb) {
    if (breadcrumb?.data?.url) {
      breadcrumb.data.url = stripBridgeParams(breadcrumb.data.url);
    }
    if (breadcrumb?.data?.to) {
      breadcrumb.data.to = stripBridgeParams(breadcrumb.data.to);
    }
    if (breadcrumb?.data?.from) {
      breadcrumb.data.from = stripBridgeParams(breadcrumb.data.from);
    }
    return breadcrumb;
  },
});

// A reload keeps the stored session and never calls saveSession, so identify
// the user here as well — without this every event after the first page load
// of a session is anonymous. Id only, matching saveSession.
try {
  const storedUser = decrypt(localStorage.getItem("user")) || {};
  if (storedUser.userId) {
    Sentry.setUser({ id: storedUser.userId });
  }
} catch (_) {
  /* ignore */
}

// -------------------------------------------------------------------------
// Auth bridge — must run before React renders (Authentication.jsx calls
// history.replace("/") synchronously during render)
// -------------------------------------------------------------------------
consumeAuthBridge();

// -------------------------------------------------------------------------
// Shared-cookie auto-login — symmetric counterpart to Sandbox's
// bootstrapSessionFromCookie. Runs AFTER the explicit redirect bridge above so
// the bridge stays the primary hand-off; this covers fresh-tab / cross-tab
// login from the shared cy-sso cookies. Also re-adopts the cookie token live
// when another tab (e.g. a Portal profile switch) bumps it.
// -------------------------------------------------------------------------
bootstrapSessionFromCookie();
registerAuthSyncListener();

// One-shot "session expired" toast, set by Core/sessionExpired.js before its
// full-page redirect to the login page. Fired here so it survives the reload.
try {
  if (sessionStorage.getItem(SESSION_EXPIRED_FLAG)) {
    sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
    setTimeout(() => toast.error(SESSION_EXPIRED_MESSAGE), 0);
  }
} catch (_) {
  /* ignore */
}

// -------------------------------------------------------------------------
// Render
// -------------------------------------------------------------------------
let baseTheme = createMuiTheme();
baseTheme = responsiveFontSizes(baseTheme);

ReactDOM.render(
  <React.StrictMode>
    <ThemeProvider theme={baseTheme}>
      <Provider store={store}>
        <Sentry.ErrorBoundary fallback={({ eventId }) => <ErrorFallback eventId={eventId} />}>
          <App />
        </Sentry.ErrorBoundary>
      </Provider>
    </ThemeProvider>
  </React.StrictMode>,
  document.getElementById("root")
);
